import { useCallback, useEffect, useRef } from 'react'
import type { useStoreApi } from '@xyflow/react'
import { canvasCounterScale, hintOpacity, selectionStrokeWidth } from '../utils/canvasViewport'

/**
 * **Der Zoom-Gegenfaktor als CSS-Variable** — aus `CanvasFlow` gezogen
 * (2026-09-05, siehe `.claude/tasks/large-files.md`).
 *
 * Ein abgeschlossener Effekt: Er meldet sich an React Flows Store an und
 * schreibt bei jeder Zoomstufe die Variablen, an denen Rahmen, Ziehpunkte und
 * Punktraster hängen. Zurück kommt der Ref für das Raster-Element und die
 * Funktion, mit der ein Auswahlwechsel das Schreiben erzwingt.
 */
export function useCanvasZoomVars(
  canvasElement: HTMLElement | null,
  storeApi: ReturnType<typeof useStoreApi>,
  /** Rasterweite und Punktradius — sie gehören dem Canvas, nicht dem Zoom. */
  gridSize: number,
  dotRadius: number,
) {
  const applyZoomVarsRef = useRef<(force?: boolean) => void>(() => {})
  const gridRef = useRef<HTMLDivElement>(null)

  /*
    Der Zoom-Gegenfaktor als CSS-Variable — am AUSGEWÄHLTEN Node und am
    Viewport-Portal (dort liegen die Ziehpunkte), nicht am Canvas-Element.

    **Über eine Store-Anmeldung und nicht über `useStore`**, und das ist der
    ganze Trick: `useStore` ließe `CanvasFlow` bei jeder Zoomstufe neu rendern
    — samt aller Notizen. Dasselbe gilt für ein `useStore` je Notiz, das bei
    jedem Store-Update über alle liefe. Hier wird stattdessen ein Attribut
    geschrieben; React sieht davon nichts, CSS erledigt den Rest.

    **Warum nicht mehr am Canvas-Element:** Eine geerbte Custom Property, die
    sich ändert, lässt den Browser den Stil ALLER Nachkommen neu rechnen — bei
    hundert Zetteln ein paar tausend Elemente je Zoom-Frame, obwohl nur der
    ausgewählte etwas davon zeigt (Auswahlrahmen, Ziehpunkte, Kantenfarbe des
    Eselsohrs). Nicht ausgewählte Zettel fallen auf die Vorgaben in der SCSS
    zurück, die dort ohnehin unsichtbar sind.

    Die Deckkraft der **Randpfeile** geht aus demselben Grund an deren
    Behälter statt ans Canvas-Element.

    Der Auswahlwechsel zieht nach (`selectionKey` unten): Ein frisch
    ausgewählter Node trägt die Werte noch nicht, und bis zur nächsten
    Zoomstufe sähe sein Rahmen bei Zoom ≠ 1 falsch aus.
  */

  useEffect(() => {
    if (!canvasElement) return

    let last = -1
    let lastX = NaN
    let lastY = NaN
    const apply = (force = false) => {
      const grid = gridRef.current
      const [x, y, zoom] = storeApi.getState().transform

      /*
        Das Punktraster folgt dem Ausschnitt — in jedem Frame, denn beim
        Pannen ändern sich nur `x` und `y`. Dieselbe Lage wie React Flows
        Pattern: Kachel = Raster × Zoom, Punktradius = `dotRadius` × Zoom, und
        die Kachel um `x % Kachel` verschoben. Der Gradient legt seinen Punkt
        in die KachelMITTE, React Flows Pattern an den Kachelanfang — daher
        die Kachel zurück plus Radius.
      */
      if (grid && (x !== lastX || y !== lastY || zoom !== last)) {
        const tile = gridSize * zoom || 1
        const radius = dotRadius * zoom
        grid.style.backgroundSize = `${tile}px ${tile}px`
        grid.style.backgroundPosition = `${x % tile + radius - tile}px ${y % tile + radius - tile}px`
        grid.style.setProperty('--canvas-grid-r', `${radius}px`)
        lastX = x
        lastY = y
      }

      if (zoom === last && !force) return
      last = zoom
      const scale = String(canvasCounterScale(zoom))
      const frame = `${selectionStrokeWidth(zoom)}px`

      // Nodes, die die Werte tragen, aber nicht mehr ausgewählt sind, geben
      // sie ab — sonst bliebe ein veralteter Wert stehen.
      for (const stale of canvasElement.querySelectorAll<HTMLElement>('.react-flow__node[data-zoom-vars]:not(.selected)')) {
        stale.style.removeProperty('--canvas-scale')
        stale.style.removeProperty('--canvas-frame-width')
        delete stale.dataset.zoomVars
      }
      for (const node of canvasElement.querySelectorAll<HTMLElement>('.react-flow__node.selected')) {
        node.style.setProperty('--canvas-scale', scale)
        // Und die Strichstärke des **Auswahlrahmens**, die beim Herauszoomen
        // ein wenig nachgibt. Dieselbe Zahl bekommt der gemeinsame Rahmen der
        // Mehrfachauswahl (`SelectionOverlay`) — eine ausgewählte Notiz soll
        // nicht anders aussehen als dieselbe Notiz in einer Gruppe.
        node.style.setProperty('--canvas-frame-width', frame)
        node.dataset.zoomVars = ''
      }
      const portal = canvasElement.querySelector<HTMLElement>('.react-flow__viewport-portal')
      portal?.style.setProperty('--canvas-scale', scale)
      portal?.style.setProperty('--canvas-frame-width', frame)
      // Die Deckkraft der **Randpfeile**, die beim Herauszoomen ZUNIMMT —
      // gegenläufig zum Strich darüber, die Begründung steht an der Funktion.
      // Als Prozentwert, weil `color-mix` ihn so erwartet.
      const hints = canvasElement.querySelector<HTMLElement>('.canvas-offscreen-hints')
      hints?.style.setProperty('--canvas-hint-tint', `${hintOpacity(zoom) * 100}%`)
    }

    applyZoomVarsRef.current = apply
    apply(true)
    const unsubscribe = storeApi.subscribe(() => apply())
    return () => {
      unsubscribe()
      applyZoomVarsRef.current = () => {}
    }
  }, [canvasElement, storeApi, gridSize, dotRadius])
  /** Ein Auswahlwechsel schreibt die Werte neu — der frische Node trägt sie noch nicht. */
  const applyZoomVars = useCallback((force?: boolean) => applyZoomVarsRef.current(force), [])

  return { gridRef, applyZoomVars }
}
